import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import Logo from '@/components/atoms/logo/logo';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import prefixSvgIds from '@/components/utilities/svg-prefixer/svg-prefixer';
import { useSearchContext } from '@/libraries/contexts/search-context';
import { getBrandClassName } from '@/libraries/getters/get-brand-class-name';
import { getLogoName } from '@/libraries/getters/get-logo-name';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './main-navigation-shared-brands-dropdown.module.scss';

export interface MainNavigationSharedBrandsDropdownProps {
  /** An array of sub brand items to switch context */
  brandNavigationItems: Content.Properties.BrandNavigationItem[];
  /** Indicates whether the sub brands dropdown is open */
  isSubBrandsMenuOpen?: boolean;
}

const MainNavigationSharedBrandsDropdown = ({
  isSubBrandsMenuOpen,
  brandNavigationItems,
}: MainNavigationSharedBrandsDropdownProps) => {
  const { setBrand } = useSearchContext();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const items = brandNavigationItems.filter(
    brandNavigationItem => !brandNavigationItem.isSelected,
  );

  const handleSubBrandClick = (brand: Content.Properties.Brand) => {
    setBrand(brand);
  };

  useEffect(() => {
    // prefix any svg ids within this dropdown to avoid collisions
    prefixSvgIds(wrapperRef.current, 'brandLogo');
  }, [brandNavigationItems]);

  return (
    <div
      ref={wrapperRef}
      className={classNames(styles.component, {
        [styles.isSubBrandsMenuOpen]: isSubBrandsMenuOpen,
      })}
    >
      <div className={styles.items}>
        {items.map((item, itemIndex) => (
          <Link
            key={itemIndex}
            className={styles.subBrandLink}
            onClick={() =>
              handleSubBrandClick(item.brand as Content.Properties.Brand)
            }
            url={item.homePageLink.url}
          >
            <Logo
              className={styles.logo}
              name={getLogoName({
                brand: item.brand as Content.Properties.Brand,
                type: 'Symbol',
              })}
            />

            <Typography
              className={getBrandClassName(
                item.brand as Content.Properties.Brand,
                styles,
              )}
              tagStyle="bodyMediumBold"
            >
              {item.name}
            </Typography>
          </Link>
        ))}
      </div>
    </div>
  );
};

MainNavigationSharedBrandsDropdown.displayName =
  'MainNavigationSharedBrandsDropdown';

export default MainNavigationSharedBrandsDropdown;
